import React, { useId } from 'react';
import { Box, IconButton, Sheet, Tooltip, Typography } from '@mui/joy';
import { HelpOutline } from '@mui/icons-material';

export function SectionHelp({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Tooltip title={children} placement="top" variant="solid" sx={{ maxWidth: 320 }}>
      <IconButton size="sm" variant="plain" color="neutral" aria-label={`Help: ${label}`}>
        <HelpOutline fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

/** A shared visual boundary between a section's purpose and its controls. */
export function AccountSection({
  id,
  title,
  description,
  icon,
  help,
  action,
  children,
}: {
  id?: string;
  title: string;
  description: string;
  icon?: React.ReactNode;
  help?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const headingId = useId();
  return (
    <Sheet
      component="section"
      id={id}
      aria-labelledby={headingId}
      variant="outlined"
      sx={{
        borderRadius: 'lg',
        overflow: 'hidden',
        minWidth: 0,
        scrollMarginTop: '100px',
        boxShadow: 'sm',
        bgcolor: 'background.surface',
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 2,
          bgcolor: 'background.level1',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 1.5,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        {icon && (
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              p: 1,
              borderRadius: 'md',
              bgcolor: 'primary.softBg',
              color: 'primary.softColor',
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography id={headingId} component="h3" level="title-lg">
              {title}
            </Typography>
            {help && <SectionHelp label={title}>{help}</SectionHelp>}
          </Box>
          <Typography level="body-sm" sx={{ mt: 0.5, color: 'text.secondary', maxWidth: '85ch' }}>
            {description}
          </Typography>
        </Box>
        {action}
      </Box>
      <Box sx={{ p: { xs: 2, md: 2.5 }, display: 'grid', gap: 2.5, minWidth: 0 }}>{children}</Box>
    </Sheet>
  );
}
